import { useInfiniteQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { memo, useCallback, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import { Card } from '@doger/ui/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@doger/ui/components/ui/dialog';
import { Skeleton } from '@doger/ui/components/ui/skeleton';

import { $$privateApi } from '@doger/admin/shared/api';
import type { Note } from '@doger/admin/shared/api/types';

import styles from './notes-modal.module.scss';

type NotesModalProps = {
  userId: string;
  isOpen: boolean;
  onClose: () => void;
};

export const NotesModal = memo<NotesModalProps>(
  ({ userId, isOpen, onClose }) => {
    const {
      data,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      isLoading,
      error,
    } = useInfiniteQuery({
      queryKey: ['user-notes', userId],
      queryFn: ({ pageParam = 0 }) =>
        $$privateApi.findManyNote({ userId, take: 10, skip: pageParam * 10 }),
      initialPageParam: 0,
      getNextPageParam: (lastPage: Note[], allPages: Note[][]) => {
        if (lastPage.length < 10) return undefined;

        return allPages.length;
      },
      enabled: isOpen,
    });

    const { ref, inView } = useInView({
      threshold: 0,
    });

    const loadMore = useCallback(() => {
      if (inView && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    // Load more when the last item comes into view
    useEffect(() => {
      loadMore();
    }, [loadMore]);

    const notes = data?.pages.flatMap((page) => page) ?? [];

    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className={styles.dialogContent}>
          <DialogHeader>
            <DialogTitle>User Notes</DialogTitle>
          </DialogHeader>

          <DialogBody className={styles.content}>
            {isLoading ? (
              <div className={styles.skeletonContainer}>
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={index} className={styles.noteSkeleton} />
                ))}
              </div>
            ) : error ? (
              <div className={styles.error}>
                Error loading notes: {error.message}
              </div>
            ) : notes.length === 0 ? (
              <div className={styles.empty}>No notes found for this user.</div>
            ) : (
              <>
                {notes.map((note) => (
                  <NoteItem key={note.id} note={note} />
                ))}

                {/* Intersection observer target for infinite scroll */}
                {hasNextPage && (
                  <div ref={ref} className={styles.loadMoreTrigger}>
                    {isFetchingNextPage && (
                      <div className={styles.loadingIndicator}>
                        <Skeleton className={styles.loadingSkeleton} />
                        <Skeleton className={styles.loadingSkeleton} />
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>
    );
  }
);

type NoteItemProps = {
  note: Note;
};

const NoteItem = memo<NoteItemProps>(({ note }) => {
  const creatorName = note.Creator ? note.Creator.email : 'N/A';

  return (
    <Card className={styles.noteCard}>
      <div className={styles.noteHeader}>
        <div className={styles.noteMeta}>
          <span className={styles.creator}>Created by: {creatorName}</span>
          <span className={styles.date}>
            {format(new Date(note.createdAt), 'MMM dd, yyyy HH:mm')}
          </span>
        </div>
      </div>
      <div className={styles.noteContent}>{note.content}</div>
    </Card>
  );
});
